import { useState } from 'react';
import { Zap, AlertTriangle, Beaker } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';

interface ExperimentalFeature {
  id: string;
  name: string;
  description: string;
  status: 'alpha' | 'beta' | 'experimental';
  enabled: boolean;
  risks: string[];
  benefits: string[];
}

const mockFeatures: ExperimentalFeature[] = [
  {
    id: 'ai-optimization',
    name: 'AI-Powered Cost Optimization',
    description: 'Automatically optimize API calls and token usage using machine learning',
    status: 'beta',
    enabled: false,
    risks: ['May affect response quality', 'Requires model retraining'],
    benefits: ['20-30% cost reduction', 'Adaptive optimization']
  },
  {
    id: 'predictive-scaling',
    name: 'Predictive Auto-scaling',
    description: 'Predict traffic patterns and pre-scale resources',
    status: 'alpha',
    enabled: false,
    risks: ['May cause over-provisioning', 'False positive predictions'],
    benefits: ['Better performance', 'Reduced cold starts']
  },
  {
    id: 'multi-model-routing',
    name: 'Smart Model Routing',
    description: 'Route requests to optimal models based on content analysis',
    status: 'experimental',
    enabled: true,
    risks: ['Routing errors', 'Increased latency'],
    benefits: ['Cost optimization', 'Quality improvement']
  }
];

export function ExperimentalFeatures() {
  const [features, setFeatures] = useState(mockFeatures);

  const toggleFeature = (id: string) => {
    setFeatures(prev => prev.map(feature => 
      feature.id === id ? { ...feature, enabled: !feature.enabled } : feature
    ));
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'alpha':
        return 'bg-red-100 text-red-800';
      case 'beta':
        return 'bg-yellow-100 text-yellow-800';
      case 'experimental':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'alpha':
        return <AlertTriangle className="w-4 h-4 text-red-500" />;
      case 'beta':
        return <Zap className="w-4 h-4 text-yellow-500" />;
      case 'experimental':
        return <Beaker className="w-4 h-4 text-purple-500" />;
      default:
        return null;
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Beaker className="w-5 h-5 text-purple-500" />
            <CardTitle>Experimental Features</CardTitle>
          </div>
          <div className="text-sm text-muted-foreground">
            Enable cutting-edge features in development. Use with caution in production.
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {features.map((feature) => (
              <Card key={feature.id} className="p-4">
                <div className="space-y-4">
                  <div className="flex items-start justify-between">
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        {getStatusIcon(feature.status)}
                        <h3 className="font-medium text-sm">{feature.name}</h3>
                        <Badge className={getStatusColor(feature.status)}>
                          {feature.status}
                        </Badge>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {feature.description}
                      </p>
                    </div>
                    <Switch
                      checked={feature.enabled}
                      onCheckedChange={() => toggleFeature(feature.id)}
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <h4 className="text-xs font-medium text-green-700 mb-2">Benefits</h4>
                      <ul className="space-y-1">
                        {feature.benefits.map((benefit, index) => (
                          <li key={index} className="text-xs text-green-600">
                            • {benefit}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <h4 className="text-xs font-medium text-red-700 mb-2">Risks</h4>
                      <ul className="space-y-1">
                        {feature.risks.map((risk, index) => (
                          <li key={index} className="text-xs text-red-600">
                            • {risk}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>

                  {feature.enabled && (
                    <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                      <div className="text-xs font-medium text-blue-800">
                        Feature Enabled
                      </div>
                      <div className="text-xs text-blue-700 mt-1">
                        This experimental feature is currently active. Monitor system performance carefully.
                      </div>
                    </div>
                  )}
                </div>
              </Card>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Feature Request</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-sm text-muted-foreground mb-4">
            Have an idea for a new experimental feature? Let us know!
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm">
              Submit Feature Request
            </Button>
            <Button variant="outline" size="sm">
              View Roadmap
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}